import React, { useState, useEffect, useContext } from "react";
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";

import moment from "moment";
import { apiGet } from "app/Services/ApiService";
import { apiUrls } from "common/ApiUrls";
import { PrivateRoutesContext } from "app/PrivateRoutesContext";
import { Utils } from "common/Utils";

import LoaderAnimation from "pages/LoaderAnimation";
import TimeSheetsTable from "components/Content/Table/TimeSheetsTable";

export default function ComplianceHours ({isActive, complianceId, setComplianceHoursComplete, isViewOnly, completedMessage}) {
    const { error } = useContext(PrivateRoutesContext);

    const [complianceHours, setComplianceHours] = useState(null);
    
    const [formattedHWBenefitDate, setFormattedHWBenefitDate] = useState("");
    const [isLoadingComplianceHours, setIsLoadingComplianceHours] = useState(false);
    const [isEmployeeCountApproved, setIsEmployeeCountApproved] = useState(false);
    const [hourTypeApproval, setHourTypeApproval] = useState([]);
    const [doShowTimeSheetsDialog, setDoShowTimeSheetsDialog] = useState(false);

    useEffect(() => {
        if (isActive || isViewOnly) {
            getComplianceHours();
        }
    },[isActive, isViewOnly, complianceId])

    useEffect(() => {
        if (complianceHours) {
            setFormattedHWBenefitDate(complianceHours.currentPeriod ? moment(complianceHours.currentPeriod).format("YYYY-MM-DD") : "");

            setIsEmployeeCountApproved(false);
            setHourTypeApproval(complianceHours.hours.map(r => !r.doRequireApproval))
        }
        else {
            setFormattedHWBenefitDate("");
            setIsEmployeeCountApproved(false);
            setHourTypeApproval([]);
        }
    },[complianceHours])

    useEffect(() => {
        var canComplete = !(hourTypeApproval && hourTypeApproval.length > 0 && hourTypeApproval.every(c => c) && (isEmployeeCountApproved || !(complianceHours && complianceHours.doRequireApproval)));

        setComplianceHoursComplete(canComplete);
    },[isEmployeeCountApproved, complianceHours, hourTypeApproval]);

    const getComplianceHours = () => {
        if (complianceId) {
            setIsLoadingComplianceHours(true);
            setComplianceHours(null);

            apiGet(apiUrls.getComplianceHours(complianceId)).then(response => {
                if (response.data) {
                    setComplianceHours(response.data);
                }
            }).catch(response => {
                console.log(response, "error");
                error.setTitle("An Error Occurred");
                error.setText("An Error occurred loading hours.");
                error.setDoShowError(true);
            }).finally(() => {
                setIsLoadingComplianceHours(false);
            });
        }
    }

    return (
        <Box>
            <Box className="container ">
                {isLoadingComplianceHours ?
                    <LoaderAnimation />
                : complianceHours ?
                <>
                    <Box className="row">
                        <Box className="col"></Box>
                        <Box className="col-2"></Box>
                        <Box className="col-2 border border-dark">
                            <strong>Current: {Utils.formatDate(complianceHours.currentPeriod)}</strong>
                        </Box>
                        <Box className="col-2 border border-dark">
                            <strong>Prior: {Utils.formatDate(complianceHours.priorPeriod)}</strong>
                        </Box>
                        <Box className="col-1 border border-dark">
                            <strong>Approve<sup>1</sup></strong>
                        </Box>
                        <Box className="col"></Box>
                    </Box>
                    <Box className="row">
                        <Box className="col"></Box>
                        <Box className="col-2 border border-dark">
                            Employees with Hours
                        </Box>
                        <Box className="col-2 border border-dark text-end">
                            {complianceHours.currentPeriodEmployees}
                        </Box>
                        <Box className="col-2 border border-dark text-end">
                            {complianceHours.priorPeriodEmployees}
                        </Box>
                        <Box className="col-1 border border-dark text-center">
                                {complianceHours.doRequireApproval ? 
                                    <Checkbox
                                        checked={isEmployeeCountApproved || isViewOnly}
                                        onChange={() => setIsEmployeeCountApproved((prevState) => !prevState)}
                                        disabled={isViewOnly}
                                    />
                                : null}
                        </Box>
                        <Box className="col"></Box>
                    </Box>
                    <Box className="row">
                        <Box className="col"></Box>
                        <Box className="col-2 border border-dark text-center">
                            <strong>Hours</strong>
                        </Box>
                        <Box className="col-1 border border-dark text-center">
                            <strong>Total</strong>
                        </Box>
                        <Box className="col-1 border border-dark text-center">
                            <strong>Retro</strong>
                        </Box>
                        <Box className="col-2 border border-dark text-center">
                            <strong>Total</strong>
                        </Box>
                        <Box className="col-1 border border-dark text-center"></Box>
                        <Box className="col"></Box>
                    </Box>
                    {complianceHours.hours.map((h,i) => (
                        <Box className="row">
                            <Box className="col"></Box>
                            <Box className="col-2 border border-dark text-center">
                                {h.hourType}
                            </Box>
                            <Box className="col-1 border border-dark text-end">
                                {h.totalCurrentHours}
                            </Box>
                            <Box className="col-1 border border-dark text-end">
                                {h.retroCurrentHours}
                            </Box>
                            <Box className="col-2 border border-dark text-end">
                                {h.totalPriorHours}
                            </Box>
                            <Box className="col-1 border border-dark text-center">
                                {h.doRequireApproval ? 
                                    <Checkbox
                                        checked={hourTypeApproval[i] || isViewOnly}
                                        onChange={() => setHourTypeApproval((prevState) => {
                                            let s = [...prevState];
                                            s[i] = !s[i];
                                            return s;
                                        })}
                                        disabled={isViewOnly}
                                    />
                                : null}
                            </Box>
                            <Box className="col"></Box>
                        </Box>))}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2}}>
                        <strong><sup>1</sup>Note: All deviations above 10% require approval</strong>
                    </Box>
                    {completedMessage ? 
                        <Box sx={{ display: 'flex', justifyContent: 'center'}}>
                            <h3>{completedMessage}</h3>
                        </Box>
                    : null}
                </> : null}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'right'}}>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setDoShowTimeSheetsDialog(true)}
                    disabled={complianceHours == null}
                >
                    See Details
                </Button>
            </Box>
            <Dialog
                open={doShowTimeSheetsDialog}
                onClose={() => setDoShowTimeSheetsDialog(false)}
                fullWidth={true}
                maxWidth="xl"
            >
                <DialogTitle id="alert-dialog-title">
                    {`Time Sheets${complianceHours && complianceHours.currentPeriod ? ` - ${Utils.formatDate(complianceHours.currentPeriod)}` : ""}`}
                </DialogTitle>
                <DialogContent>
                    <TimeSheetsTable payDate={formattedHWBenefitDate} isLoading={false} canDownload={true} />
                </DialogContent>

                <DialogActions>
                    <Button variant="outlined" onClick={() => setDoShowTimeSheetsDialog(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}